/**
 * scrub.ts — client-side PII scrub for error events.
 *
 * Defense-in-depth: the server also scrubs; the client scrub reduces the
 * surface area of accidental PII that ever leaves the browser.
 *
 * Contract (§5):
 *   - Truncate FIRST, then redact (prevents byte-slicing splitting a token).
 *   - Linear-time regexes only (ReDoS hygiene).
 *   - VIN alphabet: A-HJ-NPR-Z0-9 (matches gg-shared/redaction.rs — no I, O, Q).
 *   - Raw pre-scrub values must NEVER be logged.
 *
 * Replacement tokens:
 *   [email]  — email addresses
 *   [vin]    — 17-char VINs (A-HJ-NPR-Z0-9, word-bounded)
 *   [token]  — JWT-shaped strings; long opaque base64url tokens; 28-char UID-ish
 *   [num]    — 7+ digit numbers
 *   [uid]    — 28-char alphanumeric (UID-ish) — ordered AFTER [token] so JWTs
 *              are handled by the JWT pattern first
 */

const MAX_MESSAGE = 1024; // §3 cap: 1 KiB
const MAX_STACK = 4096; // §3 cap: 4 KiB

// ---------------------------------------------------------------------------
// Truncation helpers — char-safe (JS strings are UTF-16 code units, and all
// pattern chars are ASCII, so slicing at these byte-equivalent positions is safe).
// ---------------------------------------------------------------------------

function truncate(s: string, maxLen: number): string {
  if (s.length <= maxLen) return s;
  return s.slice(0, maxLen);
}

// ---------------------------------------------------------------------------
// Redaction patterns — order matters: JWT before UID-ish.
// ---------------------------------------------------------------------------

// email: word chars / dots / plus / dash @ host . tld
const RE_EMAIL = /[\w.+-]+@[\w-]+\.[\w.-]+/g;

// VIN: exactly 17 chars from the VIN alphabet (no I, O, Q), word-bounded
const RE_VIN = /\b[A-HJ-NPR-Z0-9]{17}\b/g;

// JWT: eyJ... three base64url segments
const RE_JWT = /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;

// Long opaque token: 32+ char base64url token (catches API keys, session tokens)
const RE_LONG_TOKEN = /\b[A-Za-z0-9_-]{32,}\b/g;

// Long digit run: 7+ consecutive digits (phone, card numbers, etc.)
const RE_LONG_DIGITS = /\b\d{7,}\b/g;

// UID-ish: exactly 28 alphanumeric chars (Firebase UID shape)
const RE_UID = /\b[A-Za-z0-9]{28}\b/g;

function redact(s: string): string {
  return s
    .replace(RE_EMAIL, '[email]')
    .replace(RE_VIN, '[vin]')
    .replace(RE_JWT, '[token]')
    .replace(RE_LONG_TOKEN, '[token]')
    .replace(RE_LONG_DIGITS, '[num]')
    .replace(RE_UID, '[uid]');
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ScrubbedError {
  name: string;
  message: string;
  stack: string;
}

/**
 * Truncates then redacts an error's name/message/stack.
 * The truncate-first order is required by the contract so that a long value
 * can't bypass scrub by putting PII past the truncation boundary.
 */
export function scrubError(name: string, message: string, stack: string): ScrubbedError {
  // Truncate first (char-safe for ASCII patterns)
  const tName = truncate(name, 64);
  const tMessage = truncate(message, MAX_MESSAGE);
  const tStack = truncate(stack, MAX_STACK);

  // Then redact
  return {
    name: redact(tName),
    message: redact(tMessage),
    stack: redact(tStack),
  };
}
